import {Box, Hide, Icon, Show, Spinner, ThemeIconKey} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {ButtonHTMLAttributes} from 'react';
import {Link, LinkProps} from 'react-router-dom';

interface BaseMenuItemProps {
  icon: ThemeIconKey;
  label: string;
  isDisabled?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
}

interface TopBarMenuItemLinkProps extends BaseMenuItemProps, LinkProps {
  as?: 'link';
}

interface TopBarMenuItemButtonProps
  extends BaseMenuItemProps, ButtonHTMLAttributes<HTMLButtonElement> {
  as: 'button';
}

type TopBarMenuItemProps = TopBarMenuItemLinkProps | TopBarMenuItemButtonProps;

export function TopBarMenuItem({
  icon,
  label,
  isDisabled,
  isActive,
  isLoading,
  as = 'link',
  ...props
}: TopBarMenuItemProps) {
  return (
    <StyledMenuItem
      as={as === 'link' ? Link : 'button'}
      $isDisabled={isDisabled || isLoading}
      $isActive={isActive}
      {...props}
    >
      <Box paddingRight={1}>
        <Show when={isLoading}>
          <Spinner size="small" />
        </Show>
        <Hide when={isLoading}>
          <Icon size={4} value={icon} />
        </Hide>
      </Box>
      {label}
    </StyledMenuItem>
  );
}

/**
 * Same styles as DropdownItem from platform/components but with a link
 */
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const StyledMenuItem = styled.div<{$isDisabled?: boolean; $isActive?: boolean}>`
  text-decoration: none;
  user-select: none;
  display: flex;
  width: 100%;
  align-items: center;
  text-align: start;
  flex: auto;
  outline: 0;
  max-height: 36px;
  padding: 8px;
  font-size: ${({theme}) => theme.fontSizes.text.small};
  font-weight: ${({theme}) => theme.fontWeights.text.default};
  line-height: ${({theme}) => theme.lineHeights.text.small};
  border-radius: ${({theme}) => theme.radii.medium};
  letter-spacing: 0;
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'auto')};
  color: ${({$isDisabled, $isActive, theme}) =>
    match([$isDisabled, $isActive])
      .with([true, Pattern.any], () => theme.colors.palettes.neutral[900][40])
      .with([Pattern.any, true], () => theme.colors.palettes.blue[80][100])
      .otherwise(() => theme.colors.text.primary)};

  &:hover {
    background-color: ${({theme, $isDisabled}) =>
      $isDisabled ? theme.colors.palettes.white[10][100] : theme.colors.palettes.blue[10][100]};
    color: ${({theme, $isDisabled}) =>
      $isDisabled ? theme.colors.palettes.neutral[900][40] : theme.colors.palettes.blue[80][100]};
  }

  &:active {
    background-color: ${({theme, $isDisabled}) =>
      $isDisabled ? theme.colors.palettes.white[10][100] : theme.colors.palettes.blue[10][100]};
    color: ${({theme, $isDisabled}) =>
      $isDisabled ? theme.colors.palettes.neutral[900][40] : theme.colors.palettes.blue[80][100]};
  }

  &:focus {
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
    color: ${({theme, $isDisabled}) =>
      $isDisabled ? theme.colors.palettes.neutral[900][40] : theme.colors.palettes.blue[80][100]};
  }
`;
